import { useState } from "react";
import type { Track } from "../../api/types";
import { Banner } from "../../components/Banner";

/* More places to find gigs: platforms without an API, opened at their own search page for a role. Nothing is copied or tracked. */
export const LINKOUT_TERMS: Record<string, { label: string; q: string; guru: string; x: string }> =
  {
    uiux: {
      label: "UI/UX design",
      q: "ui ux designer",
      guru: "ui-ux-design",
      x: '("ui/ux designer" OR "ux designer" OR "product designer" OR "figma designer")',
    },
    graphic: {
      label: "Graphic design",
      q: "graphic designer",
      guru: "graphic-design",
      x: '("graphic designer" OR "logo designer" OR "brand designer")',
    },
    video: {
      label: "Video production and editing",
      q: "video editor",
      guru: "video-editing",
      x: '("video editor" OR "motion designer" OR "reels editor")',
    },
    digital: {
      label: "Digital marketing",
      q: "social media manager",
      guru: "social-media-marketing",
      x: '("social media manager" OR "digital marketer" OR "content creator")',
    },
    va: {
      label: "Virtual assistant",
      q: "virtual assistant",
      guru: "virtual-assistant",
      x: '("virtual assistant" OR "executive assistant" OR "admin assistant" OR "personal assistant")',
    },
    cx: {
      label: "Customer service and support",
      q: "customer service representative",
      guru: "customer-service",
      x: '("customer service" OR "customer support" OR "customer success" OR "support agent")',
    },
    sales: {
      label: "Sales and business development",
      q: "sales representative",
      guru: "sales",
      x: '("sales representative" OR "sales executive" OR "appointment setter" OR "SDR" OR "business development")',
    },
    bpo: {
      label: "Call centre and BPO",
      q: "call center agent",
      guru: "telemarketing",
      x: '("call center" OR "call centre" OR "contact center" OR "BPO" OR "telesales" OR "chat agent")',
    },
    data: {
      label: "Data entry and AI training",
      q: "data entry",
      guru: "data-entry",
      x: '("data entry" OR "data annotation" OR "AI trainer" OR "transcription" OR "data labeling")',
    },
  };

/* AI training and data labelling platforms. Most have no job API; the ones that do also feed the board. */
export const AI_PLATFORMS: [string, string][] = [
  ["Outlier", "https://outlier.ai"],
  ["Mercor", "https://work.mercor.com"],
  ["Remotasks", "https://www.remotasks.com"],
  ["Mindrift (Toloka)", "https://mindrift.ai"],
  ["Alignerr", "https://www.alignerr.com"],
  ["micro1", "https://www.micro1.ai/experts/opportunities"],
  ["Appen", "https://jobs.lever.co/appen"],
  ["OneForma", "https://www.oneforma.com/jobs/"],
  ["TELUS Digital AI Community", "https://www.telusdigital.com/careers/ai-community"],
  ["Clickworker", "https://www.clickworker.com/clickworker/"],
];

const TRACK_KEYS = ["uiux", "graphic", "video", "digital"];

/** The link groups for one role, with v2's exact search URLs. */
export function moreGigsLinks(key: string): [string, [string, string][]][] {
  const t = LINKOUT_TERMS[key] ?? LINKOUT_TERMS.uiux!;
  const q = encodeURIComponent(t.q);
  const hire = '(hiring OR "looking for" OR "need a") ' + t.x + " -filter:retweets";
  return [
    [
      "Freelance sites",
      [
        ["Upwork", `https://www.upwork.com/nx/search/jobs/?q=${q}`],
        ["Freelancer", `https://www.freelancer.com/jobs/?keyword=${q}`],
        ["Guru.com", `https://www.guru.com/d/jobs/skill/${t.guru}/`],
        ["PeoplePerHour", `https://www.peopleperhour.com/freelance-jobs?q=${q}`],
        ["Workana", `https://www.workana.com/jobs?query=${q}&language=en`],
        ["Truelancer", `https://www.truelancer.com/freelance-jobs?q=${q}`],
        ["Hubstaff Talent", `https://hubstafftalent.net/search/jobs?search%5Bkeywords%5D=${q}`],
        ["Contra", `https://contra.com/opportunities`],
        ["Twine", `https://www.twine.net/jobs`],
        ["Feedcoyote", `https://www.feedcoyote.com/authorization/registration`],
      ],
    ],
    [
      "Remote job boards",
      [
        ["Himalayas", `https://himalayas.app/jobs?q=${q}`],
        ["We Work Remotely", `https://weworkremotely.com/remote-jobs/search?term=${q}`],
        ["Working Nomads", `https://www.workingnomads.com/jobs?category=&tag=${q}`],
        ["LinkedIn Jobs, remote", `https://www.linkedin.com/jobs/search/?keywords=${q}&f_WT=2`],
      ],
    ],
    [
      "Jobs in Nigeria",
      [
        ["Jobberman", `https://www.jobberman.com/jobs?q=${q}`],
        ["MyJobMag", `https://www.myjobmag.com/search/jobs?q=${q}`],
        ["Hot Nigerian Jobs", "https://www.hotnigerianjobs.com/"],
        ["Indeed Nigeria", `https://ng.indeed.com/jobs?q=${q}`],
        [
          "LinkedIn Jobs, Nigeria",
          `https://www.linkedin.com/jobs/search/?keywords=${q}&location=Nigeria`,
        ],
      ],
    ],
    [
      "People posting that they’re hiring",
      [
        [
          "LinkedIn posts",
          `https://www.linkedin.com/search/results/content/?keywords=${encodeURIComponent("hiring " + t.q)}&sortBy=%22date_posted%22`,
        ],
        ["X (Twitter)", `https://x.com/search?q=${encodeURIComponent(hire)}&f=live`],
        [
          "X, Nigeria only",
          `https://x.com/search?q=${encodeURIComponent(hire + " (Nigeria OR Lagos OR Abuja)")}&f=live`,
        ],
        [
          "X, remote",
          `https://x.com/search?q=${encodeURIComponent(hire + ' (remote OR "work from home")')}&f=live`,
        ],
      ],
    ],
  ];
}

function Chip({ name, href }: { name: string; href: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener"
      className="inline-flex min-h-9 items-center rounded-[var(--radius-chip)] border-[1.5px] border-border bg-surface px-3 text-[13px] font-semibold text-text hover:border-accent hover:text-accent"
    >
      {name} ↗
    </a>
  );
}

export function MoreGigs({
  myTrack,
  trackLabels,
}: {
  myTrack: Track;
  trackLabels: Record<Track, string>;
}) {
  const [role, setRole] = useState<string>(myTrack);
  const options: [string, string][] = [
    [myTrack, `My skill: ${trackLabels[myTrack]}`],
    ...Object.entries(LINKOUT_TERMS)
      .filter(([key]) => key !== myTrack)
      .map(([key, t]): [string, string] => [
        key,
        (TRACK_KEYS.includes(key) ? "" : "Transferable: ") + t.label,
      ]),
  ];
  return (
    <section className="my-4 rounded-[var(--radius-card)] border border-border bg-surface p-5">
      <h3 className="text-[17px] text-text">More places to find gigs</h3>
      <p className="text-[13px] text-text-muted">
        These open each site's own search. Pick your skill, or any role you could do with
        transferable skills: virtual assistance, customer service, sales, call centre and BPO work,
        data entry. Some sites, like LinkedIn and X, ask you to sign in first. Gigs you find there
        won't show under My applications, but tell us about any paid work in your Friday check-in.
      </p>
      <label htmlFor="lo-role" className="mb-1.5 block text-[12.5px] font-bold text-text">
        Search for
      </label>
      <select
        id="lo-role"
        className="min-h-11 max-w-full rounded-[var(--radius-control)] border-[1.5px] border-border bg-surface px-3 text-[14px]"
        value={role}
        onChange={(event) => setRole(event.target.value)}
      >
        {options.map(([value, label]) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </select>
      {moreGigsLinks(role).map(([heading, links]) => (
        <div key={heading}>
          <p className="mb-1.5 mt-3 text-[13px]">
            <b>{heading}</b>
          </p>
          <div className="flex flex-wrap gap-2">
            {links.map(([name, href]) => (
              <Chip key={name} name={name} href={href} />
            ))}
          </div>
        </div>
      ))}
      <h3 className="mt-[18px] text-[17px] text-text">AI training and data labelling</h3>
      <p className="text-[13px] text-text-muted">
        Paid work rating, labelling and writing for AI models. It suits careful, detail-minded
        people in any track, and your own skill often counts: some projects want designers, video
        editors or writers, and some want Igbo, Yoruba, Hausa or Pidgin speakers. Each platform
        decides which countries it accepts, so check that the sign-up page lists Nigeria.
      </p>
      <div className="flex flex-wrap gap-2">
        {AI_PLATFORMS.map(([name, href]) => (
          <Chip key={name} name={name} href={href} />
        ))}
      </div>
      <Banner tone="warn">
        <b>Stay safe:</b> never pay to get a job, never buy, rent or share an account on these
        platforms (it gets you banned and often unpaid), never share your BVN or bank logins, and be
        careful with anyone who rushes you onto WhatsApp before the work is clear. Real clients pay
        you, not the other way round.
      </Banner>
    </section>
  );
}
